// @ts-nocheck
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { PhotoUploader } from "./PhotoUploader";
import { Modal } from "./Modal";
import { ArrowDownToLine, ArrowUpFromLine, Bell, Download, Edit2, GitMerge, Info, PackageMinus, PackagePlus, Plus, RefreshCw, Upload, X } from "lucide-react";
import { downloadProtected, request } from "./FunctionalApp";
async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init);
  if ((r.headers.get("content-type") || "").includes("text/csv")) return r;
  if (!r.ok) {
    const x = await r.json().catch(() => ({}));
    throw new Error(String(x.error || "Unable to save").replaceAll("_", " "));
  }
  return r.json();
}
const post = (p: string, b: any, method = "POST") =>
  api(p, {
    method,
    headers: { "content-type": "application/json" },
    body: method === "DELETE" ? undefined : JSON.stringify(b),
  });
const put = (p: string, b: any) =>
  api(p, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(b),
  });
const pretty=(value:string)=>value.replaceAll("_"," ").toLowerCase().replace(/\b\w/g,c=>c.toUpperCase());
export function AssetWorkspace({ initialId }: any) {
  const [data, setData] = useState<any>({ assets: [] }),
    [show, setShow] = useState(false),
    [collapsed, setCollapsed] = useState<Set<string>>(new Set()),
    [expanded, setExpanded] = useState<Set<string>>(new Set()),
    [detailCache, setDetailCache] = useState<Map<string, any>>(new Map()),
    [message, setMessage] = useState("");

  async function load() { setData(await api("/api/cmms/assets")); }
  useEffect(() => { load(); if (initialId) toggleDetail(initialId); }, [initialId]);

  async function importCsv(file: File) {
    const lines = (await file.text()).split(/\r?\n/).filter(Boolean),
      headers = lines.shift()!.split(",").map((x) => x.trim());
    const rows = lines.map((line) =>
      Object.fromEntries(line.split(",").map((v, i) => [headers[i], v.trim().replace(/^"|"$/g, "")])));
    const x = await post("/api/cmms/assets/import", { rows });
    setMessage(`${x.imported} assets imported`); load();
  }

  async function toggleDetail(id: string) {
    setExpanded(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    if (!detailCache.has(id)) {
      const detail = await api(`/api/cmms/assets/${id}/history`);
      setDetailCache(prev => new Map(prev).set(id, detail));
    }
  }

  async function saveMetadata(id: string, patch: Record<string, string>) {
    await api(`/api/cmms/assets/${id}/metadata`, {
      method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(patch),
    });
    load();
    setDetailCache(prev => { const m = new Map(prev); m.delete(id); return m; });
  }

  type TreeNode = { id: string; label: string; isFolder: boolean; asset?: any; children: TreeNode[] };

  const pathTree = useMemo(() => {
    const nodeMap = new Map<string, TreeNode>();
    const root: TreeNode = { id: "__root__", label: "root", isFolder: true, children: [] };
    nodeMap.set("", root);
    function getOrCreate(segs: string[]): TreeNode {
      const key = segs.join("/");
      if (nodeMap.has(key)) return nodeMap.get(key)!;
      const parent = getOrCreate(segs.slice(0, -1));
      const node: TreeNode = { id: `folder:${key}`, label: segs[segs.length - 1]!, isFolder: true, children: [] };
      parent.children.push(node); nodeMap.set(key, node); return node;
    }
    for (const a of data.assets) {
      const segs = a.path.split("/").filter(Boolean);
      if (segs.length > 1) getOrCreate(segs.slice(0, -1));
      const parentKey = segs.slice(0, -1).join("/");
      const parent = nodeMap.get(parentKey) ?? root;
      const leaf: TreeNode = { id: a.id, label: a.name, isFolder: false, asset: a, children: [] };
      parent.children.push(leaf); nodeMap.set(segs.join("/"), leaf);
    }
    return root.children;
  }, [data.assets]);

  function countAssets(nodes: TreeNode[]): number {
    return nodes.reduce((n, c) => n + (c.isFolder ? countAssets(c.children) : 1), 0);
  }
  function allFolderIds(nodes: TreeNode[]): string[] {
    return nodes.flatMap(n => n.isFolder ? [n.id, ...allFolderIds(n.children)] : []);
  }
  function toggleFolder(id: string) {
    setCollapsed(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function AssetDetailPanel({ asset }: { asset: any }) {
    const detail = detailCache.get(asset.id);
    const meta = typeof asset.metadata === "object" && asset.metadata ? asset.metadata as Record<string,string> : {} as Record<string,string>;
    const [desc, setDesc] = useState(meta.description || "");
    const [photoUrl, setPhotoUrl] = useState(meta.photoUrl || "");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusDialog, setStatusDialog] = useState(false);
    const [newStatus, setNewStatus] = useState(asset.status || "ACTIVE");
    const [statusReason, setStatusReason] = useState("");
    const currentStatus = asset.status || "ACTIVE";

    async function changeStatus(e: React.FormEvent) {
      e.preventDefault();
      await api(`/api/cmms/assets/${asset.id}/status`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason: statusReason }),
      });
      setStatusDialog(false); setStatusReason("");
      load();
      setDetailCache(prev => { const m = new Map(prev); m.delete(asset.id); return m; });
    }

    const statusColors: Record<string, string> = {
      ACTIVE: "#1a6b3f", OUT_OF_SERVICE: "#c0392b", DECOMMISSIONED: "#6c8090"
    };
    const statusLabels: Record<string, string> = {
      ACTIVE: "Active", OUT_OF_SERVICE: "Out of service", DECOMMISSIONED: "Decommissioned"
    };

    return (
      <div className="asset-detail-panel">
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
          <span className="asset-status-badge" style={{background: currentStatus === "ACTIVE" ? "#e8f8f0" : currentStatus === "OUT_OF_SERVICE" ? "#fef2f0" : "#f0f0f0", color: statusColors[currentStatus]}}>
            {statusLabels[currentStatus] || currentStatus}
          </span>
          {asset.status_reason && <small style={{color:"var(--muted)",fontSize:11}}>{asset.status_reason}</small>}
          {data.canManage && currentStatus !== "DECOMMISSIONED" && (
            <button className="secondary" style={{marginLeft:"auto",fontSize:11}} onClick={() => { setStatusDialog(!statusDialog); setNewStatus(currentStatus); }}>
              Change status
            </button>
          )}
        </div>
        {statusDialog && (
          <form className="asset-status-form" onSubmit={changeStatus}>
            <label>New status
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                <option value="ACTIVE">Active — operational</option>
                <option value="OUT_OF_SERVICE">Out of service — temporarily unavailable</option>
                <option value="DECOMMISSIONED">Decommissioned — permanently retired</option>
              </select>
            </label>
            <label>Reason (shown to customers if linked to appointments)
              <input value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="e.g. Safety inspection, equipment fault…" />
            </label>
            {newStatus === "DECOMMISSIONED" && (
              <p className="asset-status-warning">⚠️ This will permanently retire the asset, cancel all linked PPM schedules and block all future appointment bookings for linked services.</p>
            )}
            <div style={{display:"flex",gap:8}}>
              <button disabled={saving}>{saving ? "Saving…" : "Update status"}</button>
              <button type="button" className="secondary" onClick={() => setStatusDialog(false)}>Cancel</button>
            </div>
          </form>
        )}
        <div className="asset-detail-cols">
          <div className="asset-detail-info">
            {editing ? (
              <div className="asset-detail-edit">
                <label>Description<textarea rows={3} value={desc} onChange={e => setDesc(e.target.value)} /></label>
                <label>Photo URL<input value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} placeholder="https://…" /></label>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <button disabled={saving} onClick={async () => { setSaving(true); await saveMetadata(asset.id, { description: desc, photoUrl }); setSaving(false); setEditing(false); }}>Save</button>
                  <button className="secondary" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p className="asset-detail-desc">{meta.description || <em>No description recorded.</em>}</p>
                {data.canManage && <button className="secondary" onClick={() => setEditing(true)}>Edit description &amp; photo</button>}
              </>
            )}
            {!detail ? <p className="asset-detail-loading">Loading…</p> : (
              <div className="asset-detail-stats">
                <span><b>{detail.orders.length}</b><small>Work orders</small></span>
                <span><b>{detail.plans.length}</b><small>PPM schedules</small></span>
                <span><b>{detail.parts.length}</b><small>Compatible parts</small></span>
              </div>
            )}
          </div>
          {meta.photoUrl && (
            <div className="asset-detail-photo">
              <img src={meta.photoUrl} alt={asset.name} onError={e => ((e.currentTarget as HTMLImageElement).style.display = "none")} />
            </div>
          )}
        </div>
        {detail && detail.parts.length > 0 && (
          <div className="asset-detail-table">
            <h4>Compatible parts</h4>
            <table><thead><tr><th>Part</th><th>Code</th><th>Bin</th><th>Notes</th></tr></thead>
              <tbody>{detail.parts.map((p: any) => (
                <tr key={p.id}><td>{p.canonical_name}</td><td>{p.primary_code || "—"}</td><td>{p.bin_location || "—"}</td><td>{p.notes || "—"}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {detail && detail.orders.length > 0 && (
          <div className="asset-detail-table">
            <h4>Recent work orders</h4>
            <table><thead><tr><th>Number</th><th>Issue</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>{detail.orders.slice(0, 10).map((w: any) => (
                <tr key={w.id}><td>{w.number}</td><td>{w.title}</td><td>{String(w.status).replaceAll("_", " ")}</td><td>{new Date(w.reported_at).toLocaleDateString("en-GB")}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="asset-detail-actions">
          <button className="secondary" onClick={() => downloadProtected(`/api/cmms/assets/${asset.id}/qr.svg`, `asset-${asset.id}-qr.svg`)}>
            <QrCode /> Download QR label
          </button>
        </div>
      </div>
    );
  }

  function TreeNodeRow({ node, depth }: { node: TreeNode; depth: number }) {
    const isFolderCollapsed = collapsed.has(node.id);
    if (node.isFolder) {
      const assetCount = countAssets(node.children);
      const subFolders = node.children.filter(c => c.isFolder).length;
      return (
        <>
          <div className="asset-folder-row" style={{ "--depth": depth } as any} onClick={() => toggleFolder(node.id)}>
            <span className="asset-toggle">{isFolderCollapsed ? "▶" : "▼"}</span>
            <span className="asset-folder-label">{node.label}</span>
            <span className="asset-folder-meta">
              {subFolders > 0 && <em>{subFolders} sub-area{subFolders !== 1 ? "s" : ""}</em>}
              {assetCount > 0 && <em>{assetCount} asset{assetCount !== 1 ? "s" : ""}</em>}
            </span>
          </div>
          {!isFolderCollapsed && node.children.map(child => <TreeNodeRow key={child.id} node={child} depth={depth + 1} />)}
        </>
      );
    }
    const a = node.asset;
    const isExpanded = expanded.has(a.id);
    const meta = typeof a.metadata === "object" && a.metadata ? a.metadata as Record<string,string> : {} as Record<string,string>;
    const assetStatus = a.status || "ACTIVE";
    return (
      <div className={`asset-leaf-row ${assetStatus !== "ACTIVE" ? `asset-status-${assetStatus.toLowerCase().replace("_","-")}` : ""}`} style={{ "--depth": depth } as any}>
        <span className="asset-toggle" style={{cursor:"default",color:"transparent"}}>·</span>
        <span className="asset-leaf-name">
          <b>{a.name}</b>
          {meta.description && <small>{meta.description}</small>}
        </span>
        <span className="asset-type-badge">{a.type}</span>
        {assetStatus !== "ACTIVE" && <span className={`asset-status-tag asset-status-tag-${assetStatus === "DECOMMISSIONED" ? "grey" : "red"}`}>{assetStatus === "DECOMMISSIONED" ? "Decommissioned" : "Out of service"}</span>}
        <span className="asset-ref">{a.external_ref || "—"}</span>
        <button className="asset-info-btn" title="Asset info" onClick={e => { e.stopPropagation(); toggleDetail(a.id); }}><Info size={14} /></button>
      </div>
    );
  }

  return (
    <>
      <div className="func-heading">
        <div><h1>Asset hierarchy</h1><p>Create maintainable assets and import an Agility-compatible CSV register.</p></div>
        {data.canManage ? (
          <div className="asset-tools">
            <label className="func-button"><Upload />Import CSV<input type="file" accept=".csv,text/csv" hidden onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} /></label>
            <button onClick={() => setShow(!show)}><Plus />New asset</button>
          </div>
        ) : null}
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      {show ? <Modal title="New asset" onClose={() => setShow(false)}><AssetForm assets={data.assets} saved={() => { setShow(false); load(); }} /></Modal> : null}
      {/* Asset info lightbox */}
      {expanded.size > 0 && (() => {
        const assetId = Array.from(expanded)[0];
        const asset = data.assets.find((a: any) => a.id === assetId);
        if (!asset) return null;
        return (
          <div className="asset-info-overlay" onClick={() => setExpanded(new Set())}>
            <div className="asset-info-lightbox" onClick={e => e.stopPropagation()}>
              <div className="asset-info-header">
                <div>
                  <b>{asset.name}</b>
                  <small>{asset.path}</small>
                </div>
                <button className="secondary" onClick={() => setExpanded(new Set())}><X size={16} /></button>
              </div>
              <AssetDetailPanel asset={asset} />
            </div>
          </div>
        );
      })()}
      <section className="func-panel asset-tree">
        <div className="func-table-head">
          <h2>Factory assets</h2>
          {pathTree.length > 0 && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button className="secondary" onClick={() => setCollapsed(new Set(allFolderIds(pathTree)))}>Collapse all</button>
              <button className="secondary" onClick={() => setCollapsed(new Set())}>Expand all</button>
            </div>
          )}
        </div>
        {pathTree.length ? pathTree.map(node => <TreeNodeRow key={node.id} node={node} depth={0} />) : <p className="func-empty">No assets yet.</p>}
      </section>
    </>
  );
}
function AssetHistory({ data, close }: any) {
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <div>
          <h2>{data.asset.name}</h2>
          <p>{data.asset.path}</p>
        </div>
        <button className="secondary" onClick={close}>
          Close
        </button>
      </div>
      <div className="func-kpis">
        <div className="func-kpi">
          <span>Work orders</span>
          <strong>{data.orders.length}</strong>
        </div>
        <div className="func-kpi">
          <span>PPM schedules</span>
          <strong>{data.plans.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Assigned parts</span>
          <strong>{data.parts.length}</strong>
        </div>
      </div>
      <h3>Maintenance history</h3>
      {data.orders.length ? (
        data.orders.map((w: any) => (
          <article key={w.id}>
            <span>
              <b>
                {w.number} · {w.title}
              </b>
              <small>
                {new Date(w.reported_at).toLocaleDateString()} ·{" "}
                {w.engineer_name || "Unassigned"}
              </small>
            </span>
            <em>{w.status.replaceAll("_", " ")}</em>
          </article>
        ))
      ) : (
        <p className="func-empty">No work-order history for this asset.</p>
      )}
      <h3>Planned maintenance</h3>
      {data.plans.map((p: any) => (
        <article key={p.id}>
          <span>
            <b>{p.name}</b>
            <small>
              Next due {new Date(p.next_due_at).toLocaleDateString()}
            </small>
          </span>
          <em>{p.active ? "Active" : "Paused"}</em>
        </article>
      ))}
      <h3>Compatible parts</h3>
      {data.parts.map((p: any) => (
        <article key={p.id}>
          <span>
            <b>{p.canonical_name}</b>
            <small>
              {p.primary_code || "No part code"} · {p.bin_location || "No bin"}
            </small>
          </span>
          <span>{p.notes || "No assignment notes"}</span>
        </article>
      ))}
    </section>
  );
}
function AssetForm({ assets, saved }: any) {
  const [f, setF] = useState({
    name: "",
    type: "MACHINE",
    parentId: "",
    externalRef: "",
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    await post("/api/cmms/assets", f);
    saved();
  }
  return (
    <section className="func-panel simple-form">
      <h2>Create asset</h2>
      <form onSubmit={submit}>
        <label>
          Name
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </label>
        <label>
          Type
          <select
            value={f.type}
            onChange={(e) => setF({ ...f, type: e.target.value })}
          >
            <option>SITE</option>
            <option>AREA</option>
            <option>LINE</option>
            <option>MACHINE</option>
            <option>COMPONENT</option>
          </select>
        </label>
        <label>
          Parent
          <select
            value={f.parentId}
            onChange={(e) => setF({ ...f, parentId: e.target.value })}
          >
            <option value="">Top level</option>
            {assets.map((a: any) => (
              <option key={a.id} value={a.id}>
                {a.path}
              </option>
            ))}
          </select>
        </label>
        <label>
          Asset code
          <input
            value={f.externalRef}
            onChange={(e) => setF({ ...f, externalRef: e.target.value })}
          />
        </label>
        <button>Create asset</button>
      </form>
    </section>
  );
}
export function InventoryWorkspace({ initialId }: any) {
  const [data, setData] = useState<any>({ parts: [] }),
    [show, setShow] = useState(false),
    [shelfPart, setShelfPart] = useState<any>(null),
    [stockAction, setStockAction] = useState<any>(null),
    [stockForm, setStockForm] = useState({quantity:1,note:""}),
    [codePart, setCodePart] = useState<any>(null),
    [codeValue, setCodeValue] = useState(""),
    [shelfForm, setShelfForm] = useState({
      workOrderId: "",
      quantity: 1,
      note: "",
    }),
    [relationships, setRelationships] = useState<any>(null),
    [obsoletePart, setObsoletePart] = useState<any>(null),
    [editPart, setEditPart] = useState<any>(null),
    [message, setMessage] = useState("");
  async function load() {
    setData(await api("/api/cmms/inventory"));
  }
  useEffect(() => {
    load();
    if (initialId)
      api(`/api/cmms/inventory/parts/${initialId}/relationships`)
        .then(setRelationships)
        .catch((e: any) => setMessage(e.message));
  }, [initialId]);
  async function movement(part: any, event: string) {
    if (event === "JOB_SHELF") {
      setShelfPart(part);
      setShelfForm({ workOrderId: "", quantity: 1, note: "" });
      return;
    }
    setStockAction({part,event});setStockForm({quantity:1,note:""});
  }
  async function code(part: any) {
    setCodePart(part);setCodeValue("");
  }
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Inventory and stores</h1>
          <p>
            Canonical parts, equivalent codes, bin locations and an auditable
            stock ledger.
          </p>
        </div>
        <div className="asset-tools">
          <a className="func-button" href="/api/cmms/inventory/labels.csv">
            <Download />
            Brother P-touch CSV
          </a>
          {data.canManage ? (
            <button onClick={() => setShow(!show)}>
              <Plus />
              New part
            </button>
          ) : null}
        </div>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      {show ? (
        <Modal title="New part" onClose={() => setShow(false)}>
          <PartForm saved={() => { setShow(false); load(); }} />
        </Modal>
      ) : null}
      {editPart && (
        <Modal title={`Edit part · ${editPart.canonical_name}`} onClose={() => setEditPart(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              await put(`/api/cmms/inventory/parts/${editPart.id}`, {
                canonicalName: editPart.canonical_name,
                description: editPart.description || "",
                manufacturer: editPart.manufacturer || "",
                primaryCode: editPart.primary_code || "",
                binLocation: editPart.bin_location || "",
                reorderPoint: editPart.reorder_point || 0,
                photoUrl: editPart.photo_url || "",
                supplierName: editPart.supplier_name || "",
                supplierEmail: editPart.supplier_email || "",
                supplierRef: editPart.supplier_ref || "",
                orderQty: editPart.order_qty ? Number(editPart.order_qty) : null,
                packSize: editPart.pack_size ? Number(editPart.pack_size) : null,
              });
              setMessage("Part updated"); setEditPart(null); load();
            } catch (x: any) { setMessage(x.message); }
          }}>
            <label>Part name<input required value={editPart.canonical_name} onChange={e => setEditPart({...editPart, canonical_name: e.target.value})} /></label>
            <label>Manufacturer<input value={editPart.manufacturer || ""} onChange={e => setEditPart({...editPart, manufacturer: e.target.value})} /></label>
            <label>Primary code<input value={editPart.primary_code || ""} onChange={e => setEditPart({...editPart, primary_code: e.target.value})} /></label>
            <label>Bin location<input value={editPart.bin_location || ""} onChange={e => setEditPart({...editPart, bin_location: e.target.value})} /></label>
            <label>Reorder point<input type="number" min="0" value={editPart.reorder_point || 0} onChange={e => setEditPart({...editPart, reorder_point: Number(e.target.value)})} /></label>
            <label>Description<input value={editPart.description || ""} onChange={e => setEditPart({...editPart, description: e.target.value})} /></label>
            <label>Photo URL (optional)
              <input type="url" placeholder="https://example.com/photo.jpg" value={editPart.photo_url || ""} onChange={e => setEditPart({...editPart, photo_url: e.target.value})} />
              {editPart.photo_url && <img src={editPart.photo_url} alt="Part" style={{marginTop:6,maxHeight:80,borderRadius:4,objectFit:"cover"}} onError={e=>(e.currentTarget.style.display="none")} />}
            </label>
            <div style={{borderTop:"1px solid #edf2f5",paddingTop:12,marginTop:4}}>
              <b style={{fontSize:13,color:"#102a43",display:"block",marginBottom:10}}>Supplier details</b>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <label>Supplier name<input value={editPart.supplier_name || ""} onChange={e => setEditPart({...editPart, supplier_name: e.target.value})} /></label>
                <label>Supplier email<input type="email" value={editPart.supplier_email || ""} onChange={e => setEditPart({...editPart, supplier_email: e.target.value})} /></label>
                <label>Supplier reference / SKU<input value={editPart.supplier_ref || ""} onChange={e => setEditPart({...editPart, supplier_ref: e.target.value})} /></label>
                <label>Standard order qty<input type="number" min="0" step="any" value={editPart.order_qty || ""} onChange={e => setEditPart({...editPart, order_qty: e.target.value})} /></label>
                <label>Pack size (if sold in multiples)<input type="number" min="0" step="any" placeholder="e.g. 10, 25, 100" value={editPart.pack_size || ""} onChange={e => setEditPart({...editPart, pack_size: e.target.value})} /></label>
              </div>
            </div>
            <button>Save changes</button>
          </form>
        </Modal>
      )}
      {stockAction && (
        <Modal title={`${pretty(stockAction.event)} · ${stockAction.part.canonical_name}`} onClose={() => setStockAction(null)}>
          <form onSubmit={async e => {
            e.preventDefault();
            try {
              await post(`/api/cmms/inventory/parts/${stockAction.part.id}/movements`, {event:stockAction.event,quantity:stockForm.quantity,location:stockAction.part.bin_location,workOrderId:null,note:stockForm.note});
              setMessage("Stock updated"); setStockAction(null); load();
            } catch(x:any) { setMessage(x.message); }
          }}>
            <label>Quantity<input required type="number" min="0.001" step="0.001" value={stockForm.quantity} onChange={e=>setStockForm({...stockForm,quantity:Number(e.target.value)})} /></label>
            <label>Movement note<textarea rows={3} value={stockForm.note} onChange={e=>setStockForm({...stockForm,note:e.target.value})} /></label>
            <button>Record {pretty(stockAction.event).toLowerCase()}</button>
          </form>
        </Modal>
      )}
      {codePart && (
        <Modal title={`Add equivalent code · ${codePart.canonical_name}`} onClose={() => setCodePart(null)}>
          <form onSubmit={async e => {
            e.preventDefault();
            try {
              await post(`/api/cmms/inventory/parts/${codePart.id}/codes`, {code:codeValue, kind:"EQUIVALENT"});
              setCodePart(null); setCodeValue(""); setMessage("Equivalent code added"); load();
            } catch(x:any) { setMessage(x.message); }
          }}>
            <label>Manufacturer, supplier or equivalent code
              <input required value={codeValue} onChange={e=>setCodeValue(e.target.value)} placeholder="e.g. SKF 6205-2RS1, NSK 6205DDU" />
            </label>
            <button>Add code</button>
          </form>
        </Modal>
      )}
            {shelfPart && (
        <Modal title={`Reserve ${shelfPart.canonical_name} to job shelf`} onClose={() => setShelfPart(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post(`/api/cmms/inventory/parts/${shelfPart.id}/movements`, {event:"JOB_SHELF",quantity:shelfForm.quantity,workOrderId:shelfForm.workOrderId,location:"Active-job shelf",note:shelfForm.note});
              setMessage("Reserved to job shelf"); setShelfPart(null); load();
            } catch(x:any) { setMessage(x.message); }
          }}>
            <label>Work order
              <select required value={shelfForm.workOrderId} onChange={e=>setShelfForm({...shelfForm,workOrderId:e.target.value})}>
                <option value="">Choose work order…</option>
                {(data.activeWorkOrders||[]).map((wo:any)=><option key={wo.id} value={wo.id}>{wo.number}: {wo.title}</option>)}
              </select>
            </label>
            <label>Quantity<input required type="number" min="0.001" step="0.001" value={shelfForm.quantity} onChange={e=>setShelfForm({...shelfForm,quantity:Number(e.target.value)})} /></label>
            <label>Note<textarea rows={2} value={shelfForm.note} onChange={e=>setShelfForm({...shelfForm,note:e.target.value})} /></label>
            <button>Reserve to shelf</button>
          </form>
        </Modal>
      )}
      {relationships && (
        <Modal title="Machine compatibility & replacement chain" onClose={() => setRelationships(null)} width={680}>
          <PartRelationships
            data={relationships}
            close={() => setRelationships(null)}
            reload={async (id: string) => setRelationships(await api(`/api/cmms/inventory/parts/${id}/relationships`))}
          />
        </Modal>
      )}
      {obsoletePart && (
        <Modal title="Mark part obsolete" onClose={() => setObsoletePart(null)}>
          <p style={{color:"#506573",margin:0}}>Are you sure you want to mark <b>{obsoletePart.canonical_name}</b> as obsolete? This is only possible if stock is zero. The part will remain in history but can no longer be issued.</p>
          <div style={{display:"flex",gap:8}}>
            <button style={{background:"#dc2626"}} onClick={async () => {
              try {
                await post(`/api/cmms/inventory/parts/${obsoletePart.id}`, {}, "DELETE");
                setMessage("Part marked obsolete"); setObsoletePart(null); load();
              } catch(x:any) { setMessage(x.message); setObsoletePart(null); }
            }}>Mark obsolete</button>
            <button className="secondary" onClick={() => setObsoletePart(null)}>Cancel</button>
          </div>
        </Modal>
      )}
      <section className="func-panel inventory-list">
        <div className="func-table-head">
          <h2>Parts register</h2>
        </div>
        {data.parts.map((p: any) => (
          <article key={p.id}>
            {p.photo_url && (
              <img src={p.photo_url} alt={p.canonical_name}
                style={{width:48,height:48,objectFit:"cover",borderRadius:6,flexShrink:0,border:"1px solid #e0e8ed"}}
                onError={e=>(e.currentTarget.style.display="none")} />
            )}
            <span>
              <b>{p.canonical_name}</b>
              <small>
                {p.manufacturer || "No manufacturer"} ·{" "}
                {p.primary_code || "No primary code"}
              </small>
              <small>{p.codes || "No equivalents recorded"}</small>
            </span>
            <span>
              <b>{p.stock}</b>
              <small>Reorder at {p.reorder_point}</small>
            </span>
            <span>
              <b>{p.bin_location || "No bin"}</b>
              <small>{p.obsolete ? "Obsolete" : "Active"}</small>
            </span>
            {data.canManage ? (
              <div className="inv-row-actions">
                <button title="Receive stock" onClick={() => movement(p, "RECEIPT")}><PackagePlus size={16} /></button>
                <button title="Issue stock" onClick={() => movement(p, "ISSUE")}><PackageMinus size={16} /></button>
                <button title="Return stock" onClick={() => movement(p, "RETURN")}><ArrowDownToLine size={16} /></button>
                <button title="Job shelf" onClick={() => movement(p, "JOB_SHELF")}><ArrowUpFromLine size={16} /></button>
                <button title="Add equivalent code" onClick={() => code(p)}><GitMerge size={16} /></button>
                <button title="Edit part" onClick={() => setEditPart({...p})}><Edit2 size={16} /></button>
                {!p.obsolete && <button title="Mark obsolete" className="secondary" onClick={() => setObsoletePart(p)}><X size={16} /></button>}
                <button title="Linked assets & supersession" onClick={async () => setRelationships(await api(`/api/cmms/inventory/parts/${p.id}/relationships`))}><Info size={16} /></button>
              </div>
            ) : null}
          </article>
        ))}
      </section>
      <section className="func-panel inventory-list">
        <div className="func-table-head">
          <h2>Active-job shelf</h2>
          <span>Parts reserved from available stores stock</span>
        </div>
        {(data.jobShelf || []).length ? (
          (data.jobShelf || []).map((x: any) => (
            <article key={x.id}>
              <span>
                <b>{x.canonical_name}</b>
                <small>{x.primary_code || "No part code"}</small>
              </span>
              <span>
                <b>{x.quantity}</b>
                <small>Reserved</small>
              </span>
              <span>
                <b>{x.work_order_number}</b>
                <small>{x.work_order_title}</small>
              </span>
              <div>
                <button
                  onClick={async () => {
                    await post(
                      `/api/cmms/inventory/parts/${x.part_id}/movements`,
                      {
                        event: "RETURN",
                        quantity: Number(x.quantity),
                        workOrderId: x.work_order_id,
                        location: "Stores",
                        note: `Returned from ${x.work_order_number} job shelf`,
                      },
                    );
                    setMessage("Job-shelf stock returned to stores");
                    load();
                  }}
                >
                  Return to stores
                </button>
              </div>
            </article>
          ))
        ) : (
          <p className="func-empty">
            No parts are currently recorded on active-job shelves.
          </p>
        )}
      </section>
    </>
  );
}
function PartRelationships({ data, close, reload }: any) {
  const [assetId, setAssetId] = useState(""),
    [notes, setNotes] = useState("");
  async function assign(e: FormEvent) {
    e.preventDefault();
    await post(`/api/cmms/inventory/parts/${data.part.id}/assets`, {
      assetId,
      notes,
    });
    setAssetId("");
    setNotes("");
    reload(data.part.id);
  }
  async function supersede(id: string) {
    await api(`/api/cmms/inventory/parts/${data.part.id}/supersession`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ supersededBy: id || null, obsolete: Boolean(id) }),
    });
    reload(data.part.id);
  }
  return (
    <div>
      <form className="simple-form" style={{marginBottom:16}} onSubmit={assign}>
        <label>
          Compatible machine
          <select
            required
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
          >
            <option value="">Choose asset…</option>
            {data.assets.map((a: any) => (
              <option key={a.id} value={a.id}>
                {a.path}
              </option>
            ))}
          </select>
        </label>
        <label>
          Assignment notes
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Drive end only, requires adaptor…"
          />
        </label>
        <button>Assign part</button>
      </form>
      <div>
        {data.assignments.map((a: any) => (
          <article key={a.id}>
            <span>
              <b>{a.path}</b>
              <small>{a.notes || "No notes"}</small>
            </span>
          </article>
        ))}
      </div>
      <label>
        Superseded by
        <select
          value={data.part.superseded_by || ""}
          onChange={(e) => supersede(e.target.value)}
        >
          <option value="">Current part — no replacement</option>
          {data.catalog.map((p: any) => (
            <option key={p.id} value={p.id}>
              {p.canonical_name} {p.primary_code ? `(${p.primary_code})` : ""}
            </option>
          ))}
        </select>
      </label>
      {data.part.superseded_by_name ? (
        <p style={{color:"#506573",marginTop:8}}>
          This part is obsolete. Use <b>{data.part.superseded_by_name}</b>.
        </p>
      ) : null}
    </div>
  );
}
function PartForm({ saved }: any) {
  const [f, setF] = useState({
    canonicalName: "",
    description: "",
    manufacturer: "",
    primaryCode: "",
    binLocation: "",
    reorderPoint: 0,
    photoUrl: "",
  });
  const [photos, setPhotos] = useState<{url:string;name:string;mimeType:string}[]>([]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    await post("/api/cmms/inventory/parts", { ...f, photoUrl: photos[0]?.url || f.photoUrl });
    saved();
  }
  return (
    <section className="func-panel simple-form">
      <h2>Create canonical part</h2>
      <form onSubmit={submit}>
        <label>
          Part name
          <input
            required
            value={f.canonicalName}
            onChange={(e) => setF({ ...f, canonicalName: e.target.value })}
          />
        </label>
        <label>
          Manufacturer
          <input
            value={f.manufacturer}
            onChange={(e) => setF({ ...f, manufacturer: e.target.value })}
          />
        </label>
        <label>
          Primary code
          <input
            value={f.primaryCode}
            onChange={(e) => setF({ ...f, primaryCode: e.target.value })}
          />
        </label>
        <label>
          Bin location
          <input
            value={f.binLocation}
            onChange={(e) => setF({ ...f, binLocation: e.target.value })}
          />
        </label>
        <label>
          Reorder point
          <input
            type="number"
            min="0"
            value={f.reorderPoint}
            onChange={(e) =>
              setF({ ...f, reorderPoint: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Description
          <input
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </label>
        <PhotoUploader
          label="Photo (optional)"
          multiple={false}
          photos={photos.length ? photos : (f.photoUrl ? [{url: f.photoUrl, name: "Photo"}] : [])}
          onPhotos={p => { setPhotos(p); setF({...f, photoUrl: ""}); }}
          onRemove={() => { setPhotos([]); setF({...f, photoUrl: ""}); }}
        />
        <button>Create part</button>
      </form>
    </section>
  );
}
export function ReportsWorkspace() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api("/api/cmms/reports").then(setData); }, []);
  if (!data) return <p className="func-loading">Loading reports…</p>;
  const cards = [
    ["Total work orders", data.total],
    ["Active", data.active],
    ["Completed", data.completed],
    ["Avg resolution", data.avg_resolution_minutes ? `${data.avg_resolution_minutes} min` : "—"],
    ["Downtime recorded", `${Math.round(Number(data.downtime_minutes) / 60 * 10) / 10} hrs`],
    ["Temporary fixes open", data.temporary_fixes],
    ["PPMs due", data.due],
    ["PPMs overdue", data.overdue],
    ["Low-stock parts", data.low_stock],
    ["Open helpdesk tickets", data.open_tickets],
  ];
  return (
    <>
      <div className="func-heading">
        <div><h1>Maintenance reports</h1><p>Workload, downtime, PPM compliance and stores exceptions.</p></div>
        <a className="func-button" href="/api/exports/complete"><Download />Complete export</a>
      </div>
      <div className="func-kpis" style={{gridTemplateColumns:"repeat(5,1fr)"}}>
        {cards.map(([name, value]) => (
          <div className="func-kpi" key={String(name)}><span>{name}</span><strong>{value ?? 0}</strong></div>
        ))}
      </div>
      {(data.engineerLoad || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>Engineer workload</h2></div>
          <div className="func-list" style={{"--cols":"2fr 1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Engineer</span><span>Active</span><span>Completed</span><span>Avg resolution</span></div>
            {data.engineerLoad.map((e: any) => (
              <div className="func-list-row" key={e.engineer_name}>
                <span>{e.engineer_name}</span>
                <span>{e.active}</span>
                <span>{e.completed}</span>
                <span>{e.avg_resolution_minutes ? `${e.avg_resolution_minutes} min` : "—"}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {(data.ppmCompliance || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>PPM queue by schedule</h2></div>
          <div className="func-list" style={{"--cols":"2fr 2fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Schedule</span><span>Asset</span><span>Overdue</span><span>Total due</span></div>
            {data.ppmCompliance.map((p: any, i: number) => (
              <div className="func-list-row" key={i}>
                <span>{p.plan_name}</span>
                <span>{p.asset_path}</span>
                <span style={{color: p.overdue > 0 ? "var(--red)" : undefined}}>{p.overdue}</span>
                <span>{p.total_due}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {(data.temporaryRepairs || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>Open temporary repairs</h2><span style={{marginLeft:"auto",fontSize:11,color:"var(--muted)"}}>Permanent repair still required</span></div>
          <div className="func-list" style={{"--cols":"1fr 2fr 2fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Number</span><span>Issue</span><span>Asset</span><span>Assigned to</span><span>Repair due</span></div>
            {data.temporaryRepairs.map((r: any) => (
              <div className="func-list-row" key={r.number}>
                <span>{r.number}</span>
                <span>{r.title}</span>
                <span>{r.asset_path || "—"}</span>
                <span>{r.assigned_to_name || "Unassigned"}</span>
                <span style={{color: r.permanent_repair_due_at && new Date(r.permanent_repair_due_at) < new Date() ? "var(--red)" : undefined}}>
                  {r.permanent_repair_due_at ? new Date(r.permanent_repair_due_at).toLocaleDateString("en-GB") : "Not set"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {(data.lowStockParts || []).length > 0 && (
        <section className="func-panel" style={{marginTop:16}}>
          <div className="func-table-head"><h2>Low-stock parts</h2></div>
          <div className="func-list" style={{"--cols":"2fr 1fr 1fr 1fr"} as any}>
            <div className="func-list-row func-th"><span>Part</span><span>Primary code</span><span>Bin</span><span>Stock / Reorder</span></div>
            {data.lowStockParts.map((p: any) => (
              <div className="func-list-row" key={p.canonical_name}>
                <span>{p.canonical_name}</span>
                <span>{p.primary_code || "—"}</span>
                <span>{p.bin_location || "—"}</span>
                <span style={{color:"var(--red)"}}>{Number(p.stock)} / {p.reorder_point}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
export function NotificationWorkspace() {
  const [items, setItems] = useState<any[]>([]),
    [permissionState, setPermission] = useState(
      typeof Notification === "undefined"
        ? "unsupported"
        : Notification.permission,
    ),
    seen = useRef(new Set<string>());
  async function load() {
    const next = await api("/api/notifications");
    setItems(next);
    if (Notification.permission === "granted")
      for (const n of next) {
        if (!seen.current.has(n.id) && !n.read_at)
          new Notification(n.title, { body: n.body, tag: n.id });
        seen.current.add(n.id);
      }
  }
  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);
  async function enable() {
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") load();
  }
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Notifications</h1>
          <p>Breakdowns, escalations, overdue maintenance and stores alerts.</p>
        </div>
        {permissionState !== "granted" && permissionState !== "unsupported" ? (
          <button onClick={enable}>
            <Bell />
            Enable browser notifications
          </button>
        ) : null}
      </div>
      <section className="func-panel notification-list">
        {items.map((n) => (
          <button
            key={n.id}
            className={n.read_at ? "read" : ""}
            onClick={async () => {
              await post(`/api/notifications/${n.id}/read`, {});
              load();
            }}
          >
            <Bell />
            <span>
              <b>{n.title}</b>
              <p>{n.body}</p>
              <small>{new Date(n.created_at).toLocaleString()}</small>
            </span>
          </button>
        ))}
        {!items.length ? (
          <p className="func-empty">No notifications yet.</p>
        ) : null}
      </section>
    </>
  );
}
